import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, LinearProgress, Stack } from "@mui/material";
import { getAllChecklistProgress } from "../../../../../../services/onboarding";
import { tokens, radius } from "../../../../../../theme";

/**
 * Who is part-way through onboarding, on the page where you add the next one.
 *
 * The endpoint for this existed as soon as checklists did, and nothing showed
 * it — so an admin could only find out how somebody was doing by opening their
 * record, one at a time. The question people actually have is the other way
 * round: is anything stuck?
 *
 * So it is ordered by trouble rather than by name. Overdue first, then furthest
 * behind — a list sorted alphabetically would bury the one person nobody has
 * chased.
 */

const nameOf = (e) => `${e?.fname || ""} ${e?.lname || ""}`.trim() || "Unnamed";

const InFlight = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getAllChecklistProgress();
        if (!active) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setRows(
          list
            // a finished onboarding is not news; this is a list of what is left
            .filter((r) => !r.progress?.complete)
            .sort(
              (a, b) =>
                (b.overdue || 0) - (a.overdue || 0) ||
                (a.progress?.percent || 0) - (b.progress?.percent || 0)
            )
        );
      } catch {
        // the page's job is the form; this panel simply does not appear
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", padding: "1.5rem" }}>
        <CircularProgress size={20} sx={{ color: tokens.textMuted }} />
      </Box>
    );
  }

  // Nothing outstanding is a good state, and an empty panel says it better
  // than a box explaining that there is nothing in the box.
  if (!rows.length) return null;

  return (
    <Box sx={{ mt: "2rem" }}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: "0.75rem" }}>
        <Box sx={{ fontSize: "1rem", fontWeight: 600, color: tokens.text }}>
          Still onboarding
        </Box>
        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
          {rows.length} {rows.length === 1 ? "person" : "people"}
        </Box>
      </Stack>

      <Box sx={{ background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: `${radius.card}px`, overflow: "hidden" }}>
        {rows.map((row) => (
          <Stack
            key={row._id}
            direction="row"
            alignItems="center"
            spacing={2}
            onClick={() => navigate(`/app/employee-details/${row.employee?._id}/onboarding`)}
            sx={{
              padding: "1rem 1.25rem",
              borderBottom: `1px solid ${tokens.border}`,
              cursor: "pointer",
              "&:last-of-type": { borderBottom: "none" },
              "&:hover": { background: tokens.surfaceSubtle },
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text }}>
                {nameOf(row.employee)}
              </Box>
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
                {row.employee?.aliasId ? `${row.employee.aliasId} · ` : ""}
                {row.templateName || "Onboarding"}
              </Box>
            </Box>

            <Box sx={{ width: "9rem", flexShrink: 0 }}>
              <LinearProgress
                variant="determinate"
                value={row.progress?.percent || 0}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: tokens.surfaceSubtle,
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: row.overdue ? tokens.danger : tokens.accent,
                    borderRadius: 3,
                  },
                }}
              />
              <Box sx={{ mt: "0.25rem", fontSize: "0.8125rem", color: tokens.textMuted }}>
                {row.progress?.requiredSettled ?? 0} of {row.progress?.requiredTotal ?? 0} steps
              </Box>
            </Box>

            <Box sx={{ width: "6.5rem", flexShrink: 0, textAlign: "right" }}>
              {row.overdue ? (
                <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.danger }}>
                  {row.overdue} overdue
                </Box>
              ) : (
                <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>on track</Box>
              )}
            </Box>
          </Stack>
        ))}
      </Box>
    </Box>
  );
};

export default InFlight;
